import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Navbar.css";

interface NavbarProps {
  onEarlyAccessClick: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onEarlyAccessClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showGameplayDropdown, setShowGameplayDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      className={`navbar ${isScrolled ? "scrolled" : ""}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="navbar-container">
        {/* Logo */}
        <div className="navbar-logo" onClick={() => scrollToSection("landing")}>
          <img src="header-logo.webp" alt="Seekers: Shards of Imriel" />
        </div>

        {/* Social Media Links */}
        <div className="navbar-social">
          <a href="https://www.instagram.com/seekers.gg/" target="_blank" rel="noopener noreferrer" className="social-link">
            <img src="instagram-svgrepo-com.svg" alt="Instagram" style={{ width: '20px', height: '20px' }} />
          </a>
          <a href="https://www.kickstarter.com/projects/1204577492/seekers-shards-of-imriel" target="_blank" rel="noopener noreferrer" className="social-link">
            <img src="kickstarter-svgrepo-com.svg" alt="Kickstarter" style={{ width: '15px', height: '15px' }} />
          </a>
          <a href="https://discord.gg/vafSYDEuX9" target="_blank" rel="noopener noreferrer" className="social-link">
            <img src="discord-alt-svgrepo-com.svg" alt="Discord" style={{ width: '20px', height: '20px' }} />
          </a>
        </div>

        {/* Desktop Navigation */}
        <div className="navbar-nav">
          <button className="nav-link" onClick={() => scrollToSection("about")}>
            About
          </button>

          <div
            className="nav-dropdown"
            onMouseEnter={() => setShowGameplayDropdown(true)}
            onMouseLeave={() => setShowGameplayDropdown(false)}
          >
            <button className="nav-link">Gameplay</button>
            <AnimatePresence>
              {showGameplayDropdown && (
                <motion.div
                  className="dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                >
                  <button onClick={() => scrollToSection("seekers")}>
                    Seekers
                  </button>
                  <button onClick={() => scrollToSection("equipment")}>
                    Equipment
                  </button>
                  <button onClick={() => scrollToSection("tactics")}>
                    Tactics
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button className="nav-link" onClick={() => scrollToSection("arena")}>
            Arena
          </button>

          <button
            className="nav-link"
            onClick={() => scrollToSection("artwork")}
          >
            Artwork
          </button>

          <button className="nav-link" onClick={() => scrollToSection("faq")}>
            FAQ
          </button>
        </div>

        {/* Early Access Button */}
        <div className="navbar-cta">
          <button className="early-access-btn" onClick={onEarlyAccessClick}>
            Sign Up
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <div
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          className="mobile-menu"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button onClick={() => scrollToSection("about")}>About</button>
          <button onClick={() => scrollToSection("seekers")}>Seekers</button>
          <button onClick={() => scrollToSection("equipment")}>
            Equipment
          </button>
          <button onClick={() => scrollToSection("tactics")}>Tactics</button>
          <button onClick={() => scrollToSection("arena")}>Arena</button>
          <button onClick={() => scrollToSection("artwork")}>Artwork</button>
          <button onClick={() => scrollToSection("faq")}>FAQ</button>
          <button onClick={onEarlyAccessClick}>Early Access</button>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;
